import { useRef } from "react";
import type { Attachment, ContentData, VariableDef } from "@/api/content";
import { useLang } from "@/i18n/LanguageContext";
import { AttachmentEditor } from "./AttachmentEditor";

/** Markdown body + variable chips + file attachments (document & rule types). */
export function MarkdownContentEditor({
  moduleId,
  content,
  onChange,
  variables,
}: {
  moduleId: string;
  content: ContentData;
  onChange: (next: ContentData) => void;
  variables: VariableDef[];
}) {
  const { t } = useLang();
  const markdown = content.markdown ?? "";
  const attachments = content.attachments ?? [];
  const taRef = useRef<HTMLTextAreaElement>(null);

  const setMarkdown = (md: string) => onChange({ ...content, markdown: md });
  const setAttachments = (a: Attachment[]) => onChange({ ...content, attachments: a });

  function insertVariable(key: string) {
    const token = `{{${key}}}`;
    const ta = taRef.current;
    if (!ta) {
      setMarkdown(markdown + token);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    setMarkdown(markdown.slice(0, start) + token + markdown.slice(end));
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    });
  }

  return (
    <>
      <span style={{ fontWeight: 600, fontSize: 14.5, color: "var(--ink)" }}>{t("Inhoud (Markdown)", "Content (Markdown)")}</span>
      <textarea
        ref={taRef}
        className="body"
        style={{ marginTop: 8 }}
        value={markdown}
        onChange={(e) => setMarkdown(e.target.value)}
        placeholder={t("# Welkom {{employee.first_name}}\n\nGebruik **Markdown**. Klik hieronder op een variabele om die in te voegen.", "# Welcome {{employee.first_name}}\n\nUse **Markdown**. Click a variable below to insert it.")}
      />

      {variables.length > 0 && (
        <div className="var-chips">
          <span className="var-chips-label">{t("Variabelen — klik om in te voegen:", "Variables — click to insert:")}</span>
          {variables.map((v) => (
            <button key={v.key} type="button" className="var-chip" title={`{{${v.key}}} → ${v.example}`} onClick={() => insertVariable(v.key)}>
              {v.label}
            </button>
          ))}
        </div>
      )}

      <AttachmentEditor moduleId={moduleId} attachments={attachments} onChange={setAttachments} />
    </>
  );
}
